import type { UpfileAction, UpfileMode } from "#js/pure/upfile"
import { nextMode } from "#js/pure/upfile"
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "#js/util/component-hierarchy"
import type { OekakiTool } from "./types"

export type ClipboardPasteFailure =
  | "permission-denied"
  | "image-not-found"
  | "unsupported"
  | "read-error"

/** DOMからPresenterへ渡すUpfileの操作要求。業務判定はMediatorで行う。 */
export type UpfileUiInputEvent =
  | { type: "upfile.action.requested"; action: UpfileAction }
  | { type: "upfile.paint.requested"; preferredTool?: OekakiTool }
  | { type: "upfile.file-picker.requested" }

export type UpfileUiEvent =
  | UpfileUiInputEvent
  /** 既存の階層購読者との互換用。新しいViewはaction.requestedを送る。 */
  | { type: "upfile.action"; action: UpfileAction }
  | { type: "upfile.file.received"; file: File }
  | { type: "upfile.image.received"; source: "paste" | "paint"; image: Blob }
  | { type: "upfile.submit-image.ready"; image: Blob }
  | { type: "upfile.popup-collapse.changed"; value: boolean }
  | { type: "upfile.preview.changed"; value: File | null }
  | {
      type: "upfile.paste-failure.changed"
      value: ClipboardPasteFailure | null
    }
  | { type: "upfile.configuration.changed"; allowImageReplies: boolean }

export type UpfileUiEffect =
  | { type: "abort-paint" }
  | { type: "cancel-paste" }
  | { type: "clear-inputs"; clearBaseform: boolean }
  | { type: "clear-baseform" }
  | { type: "open-file-picker" }
  | { type: "open-paint"; tool: OekakiTool }
  | { type: "read-clipboard" }
  | { type: "preview-from-input" }
  | { type: "set-blob"; tool: string; image: Blob }
  | { type: "set-file"; file: File }
  | { type: "start-hacchan" }
  | { type: "paint-unavailable"; tool: OekakiTool }

export interface UpfileUiState {
  mode: UpfileMode
  isPopupFormCollapsed: boolean
  previewBlob: File | null
  pasteFailure: ClipboardPasteFailure | null
  allowImageReplies: boolean
}

export interface UpfileUiMediatorOptions {
  initialAllowImageReplies?: boolean
  getSelectedPaintTool?: () => OekakiTool
  isPaintPopupAvailable?: (tool: OekakiTool) => boolean
  isAcceptedImage?: (image: Blob) => boolean
  runEffect?: (effect: UpfileUiEffect) => void
}

const UPFILE_UI_EVENT_TYPES = new Set<UpfileUiEvent["type"]>([
  "upfile.action.requested",
  "upfile.paint.requested",
  "upfile.file-picker.requested",
  "upfile.action",
  "upfile.file.received",
  "upfile.image.received",
  "upfile.submit-image.ready",
  "upfile.popup-collapse.changed",
  "upfile.preview.changed",
  "upfile.paste-failure.changed",
  "upfile.configuration.changed",
])

export function isUpfileUiEvent(event: TypedEvent): event is UpfileUiEvent {
  return UPFILE_UI_EVENT_TYPES.has(event.type as UpfileUiEvent["type"])
}

const EMPTY_EFFECTS: readonly UpfileUiEffect[] = []

function actionEffects(
  effects: readonly UpfileUiEffect[] = EMPTY_EFFECTS,
): readonly UpfileUiEffect[] {
  return [{ type: "cancel-paste" }, ...effects]
}

function controlsAllowInput(state: UpfileUiState): boolean {
  return state.allowImageReplies && state.mode === "empty"
}

function controlsAllowPaste(state: UpfileUiState): boolean {
  return controlsAllowInput(state)
}

function controlsAllowPaint(
  state: UpfileUiState,
  action: UpfileAction,
): boolean {
  return nextMode(state.mode, action) !== state.mode
}

function withActionState(
  state: UpfileUiState,
  action: UpfileAction,
  effects: readonly UpfileUiEffect[] = EMPTY_EFFECTS,
): MediatorTransitionResult<UpfileUiState, UpfileUiEffect> {
  return {
    status: "handled",
    state: {
      ...state,
      mode: nextMode(state.mode, action),
      pasteFailure:
        action === "paste-button-clicked" ? state.pasteFailure : null,
    },
    effects: actionEffects(effects),
  }
}

export function createUpfileUiMediator(
  options: UpfileUiMediatorOptions = {},
): Mediator<UpfileUiState, UpfileUiEvent, UpfileUiEffect> {
  const getSelectedPaintTool: () => OekakiTool =
    options.getSelectedPaintTool ?? ((): OekakiTool => "axnos")
  const isPaintPopupAvailable: (tool: OekakiTool) => boolean =
    options.isPaintPopupAvailable ??
    ((tool: OekakiTool): boolean => tool === "axnos")
  const isAcceptedImage: (image: Blob) => boolean =
    options.isAcceptedImage ?? ((_image: Blob): boolean => true)

  function resolvePaintTool(
    preferredTool: OekakiTool | undefined,
  ): OekakiTool | undefined {
    const selectedTool = preferredTool ?? getSelectedPaintTool()
    if (isPaintPopupAvailable(selectedTool)) return selectedTool
    // The selected-tool command historically fell back to Axnos when Klecks
    // was selected but was not configured. An explicit Klecks command remains
    // strict so it cannot silently open a different tool.
    if (!preferredTool && selectedTool === "klecks") {
      return isPaintPopupAvailable("axnos") ? "axnos" : undefined
    }
    return undefined
  }

  function transitionAction(
    state: UpfileUiState,
    action: UpfileAction,
  ): MediatorTransitionResult<UpfileUiState, UpfileUiEffect> {
    switch (action) {
      case "file-selected":
        return controlsAllowInput(state)
          ? withActionState(state, action, [{ type: "preview-from-input" }])
          : { status: "unhandled" }
      case "image-pasted":
        return controlsAllowPaste(state)
          ? withActionState(state, action)
          : { status: "unhandled" }
      case "paste-button-clicked":
        return controlsAllowPaste(state)
          ? withActionState(state, action, [{ type: "read-clipboard" }])
          : { status: "unhandled" }
      case "paint-button-clicked":
        return transitionPaintRequest(state, "axnos")
      case "klecks-button-clicked":
        return transitionPaintRequest(state, "klecks")
      case "hacchan-button-clicked":
        if (state.mode !== "empty" && state.mode !== "waiting-hacchan") {
          return { status: "unhandled" }
        }
        return withActionState(state, action, [
          ...(state.mode === "empty"
            ? [{ type: "preview-from-input" } as const]
            : []),
          { type: "start-hacchan" },
        ])
      case "clear-button-clicked":
        return {
          status: "handled",
          state: {
            ...state,
            mode: nextMode(state.mode, action),
            previewBlob: null,
            pasteFailure: null,
          },
          effects: actionEffects([
            { type: "abort-paint" },
            { type: "clear-inputs", clearBaseform: true },
            { type: "preview-from-input" },
          ]),
        }
      case "submitted":
        return {
          status: "handled",
          state: {
            ...state,
            mode: nextMode(state.mode, action),
            previewBlob: null,
            pasteFailure: null,
          },
          effects: actionEffects([
            { type: "abort-paint" },
            { type: "clear-baseform" },
          ]),
        }
      case "paint-finished":
        return state.mode === "waiting-axnos" || state.mode === "waiting-klecks"
          ? withActionState(state, action, [{ type: "preview-from-input" }])
          : { status: "unhandled" }
    }
  }

  function transitionPaintRequest(
    state: UpfileUiState,
    preferredTool: OekakiTool | undefined,
  ): MediatorTransitionResult<UpfileUiState, UpfileUiEffect> {
    const tool = resolvePaintTool(preferredTool)
    const action =
      tool === "klecks" ? "klecks-button-clicked" : "paint-button-clicked"
    if (!tool) {
      const unavailableTool = preferredTool ?? getSelectedPaintTool()
      return {
        status: "handled",
        state: { ...state, pasteFailure: null },
        effects: actionEffects([
          { type: "paint-unavailable", tool: unavailableTool },
        ]),
      }
    }
    if (!controlsAllowPaint(state, action)) {
      return { status: "handled", state }
    }
    return withActionState(state, action, [{ type: "open-paint", tool }])
  }

  function transition(
    state: UpfileUiState,
    event: UpfileUiEvent,
  ): MediatorTransitionResult<UpfileUiState, UpfileUiEffect> {
    switch (event.type) {
      case "upfile.action.requested":
      case "upfile.action":
        return transitionAction(state, event.action)
      case "upfile.paint.requested":
        return transitionPaintRequest(state, event.preferredTool)
      case "upfile.file-picker.requested":
        return controlsAllowInput(state)
          ? {
              status: "handled",
              state,
              effects: [{ type: "open-file-picker" }],
            }
          : { status: "unhandled" }
      case "upfile.file.received":
        return controlsAllowInput(state)
          ? withActionState(state, "file-selected", [
              { type: "set-file", file: event.file },
              { type: "preview-from-input" },
            ])
          : { status: "unhandled" }
      case "upfile.image.received": {
        const isPaintResult = event.source === "paint"
        const action = isPaintResult ? "paint-finished" : "image-pasted"
        const canReceive = isPaintResult
          ? state.mode === "waiting-axnos" || state.mode === "waiting-klecks"
          : controlsAllowPaste(state)
        if (!canReceive) return { status: "unhandled" }
        if (!isAcceptedImage(event.image)) {
          return isPaintResult
            ? {
                status: "handled",
                state: {
                  ...state,
                  mode: "empty",
                  previewBlob: null,
                  pasteFailure: null,
                },
                effects: actionEffects([
                  { type: "abort-paint" },
                  { type: "clear-inputs", clearBaseform: true },
                  { type: "preview-from-input" },
                ]),
              }
            : { status: "handled", state }
        }
        return withActionState(state, action, [
          {
            type: "set-blob",
            tool: isPaintResult ? "oekaki" : "paste",
            image: event.image,
          },
          { type: "preview-from-input" },
        ])
      }
      case "upfile.submit-image.ready":
        if (state.mode !== "waiting-hacchan") return { status: "unhandled" }
        return {
          status: "handled",
          state,
          effects: [
            { type: "set-blob", tool: "oekaki98", image: event.image },
            { type: "preview-from-input" },
          ],
        }
      case "upfile.popup-collapse.changed":
        return {
          status: "handled",
          state: { ...state, isPopupFormCollapsed: event.value },
        }
      case "upfile.preview.changed":
        return {
          status: "handled",
          state: { ...state, previewBlob: event.value },
        }
      case "upfile.paste-failure.changed":
        return {
          status: "handled",
          state: { ...state, pasteFailure: event.value },
        }
      case "upfile.configuration.changed":
        return {
          status: "handled",
          state: { ...state, allowImageReplies: event.allowImageReplies },
          effects: [{ type: "cancel-paste" }],
        }
    }
  }

  return createMediator<UpfileUiState, UpfileUiEvent, UpfileUiEffect>(
    {
      mode: "empty",
      isPopupFormCollapsed: false,
      previewBlob: null,
      pasteFailure: null,
      allowImageReplies: options.initialAllowImageReplies ?? true,
    },
    transition,
    { runEffect: options.runEffect },
  )
}
